import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  CompanyPicker,
  CountryField,
  DocumentsToProvide,
  FORMALITY_COUNTRIES,
  FormalityPage,
  TextField,
  fieldError,
} from "../../../components/formalities/FormalityParts";
import { showError } from "../../../components/options/OptionParts";
import { addToCart } from "../../../redux/actions/CartActions";
import { createCnpsRegistrationRequest } from "../../../redux/actions/FormalityActions";

const CNPS_REGISTRATION_PRICE = 30000;

const FIELDS = [
  {
    name: "address",
    label: "Adresse du promoteur",
    placeholder: "Quartier, ville",
  },
  { name: "phone", label: "Téléphone du promoteur", type: "tel" },
  { name: "email", label: "Adresse e-mail du promoteur", type: "email" },
  { name: "businessName", label: "Nom de l'entreprise" },
  { name: "companyPhone", label: "Téléphone de l'entreprise", type: "tel" },
  {
    name: "companyEmail",
    label: "Adresse e-mail de l'entreprise",
    type: "email",
  },
];

// Registration of a company and its employees at the CNPS
function CnpsRegistration() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { userCompanies } = useSelector((state) => state.companies);

  const [country, setCountry] = useState(FORMALITY_COUNTRIES[0]);
  const [values, setValues] = useState({
    address: "",
    phone: user?.mobile || "",
    email: user?.email || "",
    businessName: "",
    companyPhone: "",
    companyEmail: "",
  });
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (name) => (value) =>
    setValues((previous) => ({ ...previous, [name]: value }));

  const isValid =
    country &&
    FIELDS.every((field) => !fieldError(values[field.name], field.type));

  const handleCreated = async ({ success, message }) => {
    if (!success) {
      setSubmitting(false);
      showError(Swal, message);
      return;
    }

    await dispatch(
      addToCart({ from: "cnpsRegistration", price: CNPS_REGISTRATION_PRICE }),
    );
    setSubmitting(false);
    navigate("/formality-documents");
  };

  const handleContinue = () => {
    setTouched(true);
    if (!isValid) return;

    setSubmitting(true);
    dispatch(
      createCnpsRegistrationRequest(
        user.token,
        {
          country,
          ...Object.fromEntries(
            FIELDS.map((field) => [field.name, values[field.name].trim()]),
          ),
        },
        handleCreated,
      ),
    );
  };

  return (
    <FormalityPage
      from="cnpsRegistration"
      title="Immatriculation de l'entreprise à la CNPS"
      lead="Nous immatriculons votre entreprise et ses employés à la CNPS."
      price={CNPS_REGISTRATION_PRICE}
      submitting={submitting}
      onContinue={handleContinue}
    >
      <CountryField value={country} onChange={setCountry} />

      <CompanyPicker
        companies={userCompanies}
        onPick={(company) =>
          company &&
          setValues((previous) => ({
            ...previous,
            businessName: company.businessName || "",
            companyPhone: company.mobile || previous.companyPhone,
            companyEmail: company.email || previous.companyEmail,
          }))
        }
      />

      {FIELDS.map((field) => (
        <TextField
          key={field.name}
          id={`cnps-${field.name}`}
          label={field.label}
          type={field.type}
          placeholder={field.placeholder}
          value={values[field.name]}
          onChange={set(field.name)}
          error={touched ? fieldError(values[field.name], field.type) : null}
        />
      ))}

      <DocumentsToProvide
        documents={[
          "Registre de commerce",
          "Attestation d'immatriculation",
          "Attestation de conformité fiscale",
          "Plan de localisation",
          "Liste des employés",
        ]}
      />
    </FormalityPage>
  );
}

export default CnpsRegistration;
